import { Button, Flex, Grid, Space, Stack, Table, Text } from '@mantine/core';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { NoDataFoundTableRow } from '../../components/DataTable';
import { LoadingRows } from '../../components/LoadingRows';
import { PageHeader } from '../../components/PageHeader';
import { Paper } from '../../components/Paper';
import { Stat } from '../../components/Stat';
import { Title } from '../../components/Title';
import { query } from '../../util/api/query';
import type { ReceiptViewDto } from '../../util/types/ReceiptViewDto';
import { ReceiptTableRow } from './components/ReceiptTableRow';
import { KeyedRow } from '../../components/KeyedRow';

interface ReceiptProps {
	id?: string;
	startLoading?: boolean;
}

export function Receipt({ id, startLoading = true }: ReceiptProps) {
	const { receiptId, organisation } = useParams();
	const validId = receiptId || id;

	const receiptItemsQuery = query<ReceiptViewDto>(
		`${organisation}/sales-viewer/receipt-view?receiptId=${validId}`,
		startLoading,
	);

	useEffect(() => {
		if (receiptId) {
			receiptItemsQuery.refetch();
		}
	}, []);

	const table = (
		<>
			<Table>
				<Table.Thead>
					<KeyedRow>
						<Table.Th>
							<Title label={'SKUID'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Name'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Quantity'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Profit Per Item'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Price Per Item'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Gross'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Total Vat'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Discount'} />
						</Table.Th>
					</KeyedRow>
				</Table.Thead>
				<Table.Tbody>
					{!receiptItemsQuery.isLoading ? (
						<>
							{receiptItemsQuery.data?.value.productsSold.map((element) => (
								<ReceiptTableRow key={element.name} saleItems={element} />
							))}
							{receiptItemsQuery.data?.value.productsSold.length === 0 && (
								<NoDataFoundTableRow />
							)}
						</>
					) : (
						<LoadingRows rows={1} />
					)}
				</Table.Tbody>
			</Table>
		</>
	);

	return (
		<>
			{receiptId && (
				<>
					<PageHeader title={'Receipt'} hideFiltering />
					<Stack w={'100%'}>
						<Text c={'grey'}>ID: {validId}</Text>
						<Grid w={'100%'}>
							<Grid.Col span={3}>
								<Paper>
									<Stat
										data={receiptItemsQuery.data?.value.productsSold
											.reduce<number>((accumulator, value) => {
												return accumulator + value.soldCount;
											}, 0)
											.toString()}
										label={'Items Purchased'}
										isLoading={receiptItemsQuery.isLoading}
									/>
								</Paper>
							</Grid.Col>
							<Grid.Col span={3}>
								<Paper>
									<Stat
										data={receiptItemsQuery.data?.value.receipt?.operatorName}
										label={'Operator'}
										isLoading={receiptItemsQuery.isLoading}
									/>
								</Paper>
							</Grid.Col>
							<Grid.Col span={3}>
								<Paper>
									<Stat
										data={receiptItemsQuery.data?.value.receipt?.totalAmount.toString()}
										label={'Gross Sale'}
										isFinance
										isLoading={receiptItemsQuery.isLoading}
									/>
								</Paper>
							</Grid.Col>
							<Grid.Col span={3}>
								<Paper>
									<Stat
										data={receiptItemsQuery.data?.value.receipt?.vat.toString()}
										label={'VAT'}
										isFinance
										isLoading={receiptItemsQuery.isLoading}
									/>
								</Paper>
							</Grid.Col>
						</Grid>
					</Stack>
				</>
			)}

			{!receiptId ? (
				<>
					{table}
					<Flex justify={'center'}>
						<Space h={20} />
						<Button
							variant={'subtle'}
							color={'medepos.1'}
							onClick={() => window.open(`/${organisation}/receipt/${id}`)}
						>
							Open In New Tab
						</Button>
					</Flex>
				</>
			) : (
				<Paper>{table}</Paper>
			)}
		</>
	);
}
